import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Send, 
  Paperclip, 
  Image as ImageIcon, 
  FileText, 
  Download, 
  ShieldCheck, 
  AlertTriangle, 
  Loader2, 
  Clock, 
  Check, 
  CheckCheck, 
  User, 
  Scale, 
  Lock,
  Sparkles
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from '../common/Toast';
import { localizeLawyerPosition } from '../../utils/helpers';
import AuthModal from '../auth/AuthModal';
import SubscriptionModal from '../subscription/SubscriptionModal';

export default function ClientChatModal({ 
  isOpen, 
  onClose, 
  initialLawyer = null, 
  initialConversationId = null,
  onOpenSubscription = null 
}) {
  const { user, isAuthenticated, isSubscribed, refreshUser, refreshSubscription } = useAuth();
  const { currentLang } = useLanguage();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loading, setLoading] = useState(false);

  // Modals inside chat
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [pendingText, setPendingText] = useState('');

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper to load conversation and messages if authenticated
  const loadChatData = async (convId = null) => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      let activeId = convId || initialConversationId;

      // Start or locate conversation if lawyer is specified
      if (!activeId && initialLawyer?.id) {
        const startRes = await api.post('/chat/start', {
          lawyerId: initialLawyer.id,
          subject: currentLang === 'en'
            ? `Confidential consultation with ${initialLawyer.fullName}`
            : currentLang === 'ru'
            ? `Конфиденциальная переписка с адвокатом ${initialLawyer.fullName}`
            : `${initialLawyer.fullName} bilan shaxsiy yozishma`,
        });
        if (startRes?.success && startRes?.conversation) {
          activeId = startRes.conversation.id;
          setConversation(startRes.conversation);
        }
      }

      if (activeId) {
        const res = await api.get(`/chat/conversations/${activeId}/messages`);
        if (res?.success) {
          setConversation(res.conversation);
          setMessages(res.messages || []);
        }
      }
    } catch (err) {
      console.warn('Chat load error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initialize or fetch conversation when opened
  useEffect(() => {
    if (!isOpen) {
      setConversation(null);
      setMessages([]);
      setInputText('');
      setPendingText('');
      return;
    }

    if (isAuthenticated) {
      loadChatData();
    } else {
      setConversation(null);
      setMessages([]);
      setLoading(false);
    }
  }, [isOpen, isAuthenticated, initialLawyer?.id, initialConversationId]);

  // Periodic poll for incoming replies while chat is open
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !conversation?.id) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/chat/conversations/${conversation.id}/messages`);
        if (res?.success) {
          setMessages(res.messages || []);
        }
      } catch (e) {}
    }, 4000);

    return () => clearInterval(interval);
  }, [isOpen, isAuthenticated, conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  if (!isOpen) return null;

  // Actual core message sender
  const executeSendMessage = async (textToSend) => {
    if (!textToSend || !textToSend.trim()) return;
    setSending(true);

    try {
      let activeConvId = conversation?.id || initialConversationId;

      // Ensure conversation exists on server
      if (!activeConvId && initialLawyer?.id) {
        const startRes = await api.post('/chat/start', {
          lawyerId: initialLawyer.id,
          subject: currentLang === 'en'
            ? `Confidential consultation with ${initialLawyer.fullName}`
            : currentLang === 'ru'
            ? `Конфиденциальная переписка с адвокатом ${initialLawyer.fullName}`
            : `${initialLawyer.fullName} bilan shaxsiy yozishma`,
        });
        if (startRes?.success && startRes?.conversation) {
          activeConvId = startRes.conversation.id;
          setConversation(startRes.conversation);
        } else {
          throw new Error(startRes?.message || (currentLang === 'en' ? 'Could not open chat session' : currentLang === 'ru' ? 'Не удалось открыть комнату чата' : 'Chat xonasini ochib bo‘lmadi'));
        }
      }

      if (!activeConvId) {
        throw new Error(currentLang === 'en' ? 'Chat session not found' : currentLang === 'ru' ? 'Комната чата не найдена' : 'Suhbat xonasi topilmadi');
      }

      const res = await api.post(`/chat/conversations/${activeConvId}/messages`, {
        text: textToSend.trim(),
      });

      if (res?.success && res?.message) {
        setMessages((prev) => [...prev, res.message]);
        setInputText('');
        setPendingText('');
      } else {
        throw new Error(res?.message || (currentLang === 'en' ? 'Error sending message' : currentLang === 'ru' ? 'Ошибка отправки сообщения' : 'Xabarni yuborishda xatolik'));
      }
    } catch (err) {
      showToast(err.message || (currentLang === 'en' ? 'Failed to send message' : currentLang === 'ru' ? 'Произошла ошибка при отправке сообщения' : 'Xabarni yuborishda xatolik yuz berdi'), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = (inputText || pendingText || '').trim();
    if (!text || sending) return;

    // Requirement 1: User must have an account and be logged in
    if (!isAuthenticated) {
      setPendingText(text);
      setAuthModalOpen(true);
      showToast(
        currentLang === 'en'
          ? 'Please log in or register before sending message to attorney'
          : currentLang === 'ru' 
          ? 'Для отправки сообщения войдите или зарегистрируйтесь' 
          : 'Advokatga xabar yuborish uchun avval tizimga kiring yoki ro‘yxatdan o‘ting',
        'info'
      );
      return;
    }

    // Requirement 2: User must have paid/active subscription
    if (!isSubscribed) {
      setPendingText(text);
      setSubscriptionModalOpen(true);
      showToast(
        currentLang === 'en'
          ? 'Activate 35,000 UZS subscription to engage in chat with attorney'
          : currentLang === 'ru'
          ? 'Для личной переписки с адвокатом требуется подписка (35 000 сум)'
          : 'Advokat bilan shaxsiy yozishma olib borish uchun 35 000 so‘mlik obunani faollashtiring',
        'info'
      );
      return;
    }

    // All requirements fulfilled -> send immediately
    await executeSendMessage(text);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isAuthenticated) {
      setAuthModalOpen(true);
      showToast(
        currentLang === 'en'
          ? 'Please log in or register before uploading files'
          : currentLang === 'ru'
          ? 'Для отправки файла сначала войдите или зарегистрируйтесь'
          : 'Fayl yuborish uchun avval tizimga kiring yoki ro‘yxatdan o‘ting',
        'info'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!isSubscribed) {
      setSubscriptionModalOpen(true);
      showToast(
        currentLang === 'en'
          ? 'Activate 35,000 UZS subscription to send documents'
          : currentLang === 'ru'
          ? 'Для отправки документов требуется активная подписка'
          : 'Hujjat yuborish uchun 35 000 so‘mlik obunani faollashtiring',
        'info'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Strict APK and executable check
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const forbidden = ['.apk', '.exe', '.bat', '.cmd', '.msi', '.vbs', '.php', '.jar', '.sh'];

    if (forbidden.includes(ext) || file.type.includes('package-archive')) {
      showToast(
        currentLang === 'en'
          ? 'APK and executable files are strictly prohibited! Upload images or PDF only.'
          : currentLang === 'ru'
          ? 'Загрузка APK и исполняемых файлов строго запрещена! Разрешены только фото или PDF.'
          : 'APK va dasturiy fayllarni yuklash xavfsizlik sababli qat’iyan taqiqlangan! Faqat rasm yoki PDF yuklang.',
        'error'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!file.type.startsWith('image/') && ext !== '.pdf' && file.type !== 'application/pdf') {
      showToast(
        currentLang === 'en'
          ? 'Only images (JPG, PNG, WEBP) and documents (PDF) are accepted'
          : currentLang === 'ru'
          ? 'Принимаются только изображения (JPG, PNG, WEBP) и документы (PDF)'
          : 'Faqat rasm (JPG, PNG, WEBP) va hujjat (PDF) fayllari qabul qilinadi',
        'error'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      showToast(
        currentLang === 'en'
          ? 'File size must not exceed 20MB'
          : currentLang === 'ru'
          ? 'Размер файла не должен превышать 20МБ'
          : 'Fayl hajmi 20MB dan oshmasligi kerak',
        'error'
      );
      return;
    }

    setUploadingFile(true);
    try {
      let activeConvId = conversation?.id || initialConversationId;
      if (!activeConvId && initialLawyer?.id) {
        const startRes = await api.post('/chat/start', {
          lawyerId: initialLawyer.id,
          subject: currentLang === 'en'
            ? `Confidential consultation with ${initialLawyer.fullName}`
            : currentLang === 'ru'
            ? `Конфиденциальная переписка с адвокатом ${initialLawyer.fullName}`
            : `${initialLawyer.fullName} bilan shaxsiy yozishma`,
        });
        if (startRes?.success && startRes?.conversation) {
          activeConvId = startRes.conversation.id;
          setConversation(startRes.conversation);
        }
      }

      if (!activeConvId) {
        throw new Error(currentLang === 'en' ? 'Chat session not found' : currentLang === 'ru' ? 'Комната чата не найдена' : 'Suhbat xonasi topilmadi');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', file.name);

      const res = await api.postFormData(`/chat/conversations/${activeConvId}/upload`, formData);
      if (res?.success && res?.message) {
        setMessages((prev) => [...prev, res.message]);
        showToast(
          currentLang === 'en'
            ? 'File sent securely!'
            : currentLang === 'ru'
            ? 'Файл безопасно отправлен!'
            : 'Fayl xavfsiz tarzda yuborildi!',
          'success'
        );
      }
    } catch (err) {
      showToast(err.message || (currentLang === 'en' ? 'Failed to upload file' : currentLang === 'ru' ? 'Ошибка отправки файла' : 'Faylni yuborishda xatolik'), 'error');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAuthSuccess = async () => {
    setAuthModalOpen(false);
    await refreshUser();
    const subStatus = await refreshSubscription();
    const active = Boolean(
      (user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')) ||
      (subStatus && (subStatus.isFreeExempt || subStatus.isActive))
    );

    // After auth, check subscription
    if (!active) {
      setSubscriptionModalOpen(true);
      showToast(
        currentLang === 'en'
          ? 'Activate 35,000 UZS subscription to send messages to attorney'
          : currentLang === 'ru'
          ? 'Активируйте подписку для отправки сообщения адвокату'
          : 'Xabarni advokatga yetkazish uchun 35 000 so‘mlik obunani faollashtiring',
        'info'
      );
    } else {
      if (pendingText) {
        const msg = pendingText;
        setPendingText('');
        executeSendMessage(msg);
      } else {
        loadChatData();
      }
    }
  };

  const handleSubscriptionSuccess = async (sub) => {
    setSubscriptionModalOpen(false);
    await refreshSubscription();
    if (sub?.status === 'ACTIVE' || sub?.isFreeExempt) {
      showToast(
        currentLang === 'en'
          ? 'Subscription activated! Full chat access open.'
          : currentLang === 'ru' 
          ? 'Подписка активирована! Доступ открыт.' 
          : 'Obuna faollashtirildi! Chatdan to‘liq foydalanishingiz mumkin.',
        'success'
      );
      if (pendingText) {
        const msg = pendingText;
        setPendingText('');
        executeSendMessage(msg);
      } else {
        loadChatData();
      }
    } else {
      showToast(
        currentLang === 'en'
          ? 'Payment receipt submitted for review! Chat will activate upon admin approval.'
          : currentLang === 'ru'
          ? 'Чек об оплате отправлен на проверку. Чат активируется после подтверждения администратором.'
          : 'To‘lov cheki tekshiruvga yuborildi. Administrator tasdiqlashi bilan chat faollashadi.',
        'info'
      );
    }
  };

  const partnerName = conversation?.lawyer?.fullName || initialLawyer?.fullName || (currentLang === 'en' ? 'Attorney' : currentLang === 'ru' ? 'Адвокат' : 'Advokat');
  const partnerPhoto = conversation?.lawyer?.photoUrl || initialLawyer?.photoUrl;
  const partnerRoleRaw = conversation?.lawyer?.position || initialLawyer?.position || 'Katta Advokat';
  const partnerRole = localizeLawyerPosition(partnerRoleRaw, currentLang);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity"
          onClick={onClose}
        />

        {/* Chat Window */}
        <div className="relative w-full max-w-2xl h-[92vh] sm:h-[85vh] bg-[#070c16] border border-[#c59b27]/50 rounded-2xl shadow-2xl shadow-black/95 overflow-hidden z-10 flex flex-col text-slate-200 animate-in zoom-in-95 duration-200">
          
          {/* Top Gold Accent Bar */}
          <div className="h-1 w-full bg-gradient-to-r from-amber-600 via-[#c59b27] to-amber-600 shrink-0" />

          {/* Chat Header */}
          <div className="p-3.5 sm:p-4 border-b border-[#162238] bg-[#090e18] flex items-center justify-between shrink-0">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="relative w-10 h-10 rounded-full overflow-hidden border border-[#c59b27]/60 shrink-0 bg-[#0e172a]">
                {partnerPhoto ? (
                  <img src={partnerPhoto} alt={partnerName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#c59b27]">
                    <Scale className="w-5 h-5" />
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <h3 className="font-serif text-sm sm:text-base font-bold text-slate-100 truncate">
                  {partnerName}
                </h3>
                <div className="flex items-center space-x-2 text-[10px] text-slate-400">
                  <span className="text-[#c59b27] font-semibold">{partnerRole}</span>
                  <span>•</span>
                  <span className="flex items-center text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1 animate-pulse" />
                    {currentLang === 'en' ? 'Secure Encrypted Channel' : currentLang === 'ru' ? 'Защищённый защитный канал' : 'Xavfsiz himoyalangan kanal'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="hidden sm:flex items-center text-[10px] font-semibold px-2.5 py-1 rounded bg-[#101b30] border border-[#c59b27]/30 text-[#c59b27]">
                <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                <span>{currentLang === 'en' ? 'Attorney-Client Privilege' : currentLang === 'ru' ? 'Адвокатская тайна' : 'Advokatlik siri'}</span>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-[#121c2e] hover:bg-[#1a2840] border border-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                title={currentLang === 'en' ? 'Close' : currentLang === 'ru' ? 'Закрыть' : 'Yopish'}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#050912]">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-2">
                <Loader2 className="w-6 h-6 animate-spin text-[#c59b27]" />
                <span className="text-xs">
                  {currentLang === 'en' ? 'Loading secure consultation channel...' : currentLang === 'ru' ? 'Загрузка защищённого канала чата...' : 'Xavfsiz chat kanali yuklanmoqda...'}
                </span>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6 text-slate-400 space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-[#0f1728] border border-[#1b2b46] flex items-center justify-center text-[#c59b27] shadow-lg">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h4 className="font-serif text-sm font-bold text-slate-200">
                  {currentLang === 'en' ? 'Confidential Legal Chat Started' : currentLang === 'ru' ? 'Конфиденциальная переписка с адвокатом' : 'Maxfiy Advokatlik Yozishmasi Boshlandi'}
                </h4>
                <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
                  {currentLang === 'en'
                    ? 'All messages and documents exchanged in this chat are strictly protected under attorney-client privilege. Write your inquiry or attach PDF/image documents.'
                    : currentLang === 'ru'
                    ? 'Все сообщения и отправленные документы в данном чате строго защищены законом об адвокатской тайне. Напишите ваш вопрос или прикрепите PDF/фото документ.'
                    : 'Ushbu chatdagi barcha suhbatlar va yuborilgan hujjatlar advokatlik siri qonuni bilan himoyalangan. Savolingizni yozing yoki PDF/rasm hujjat biriktiring.'}
                </p>
                {!isAuthenticated && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setAuthModalOpen(true)}
                      className="px-4 py-2 rounded-xl bg-[#111c2e] hover:bg-[#182842] border border-[#c59b27]/40 text-[#c59b27] hover:text-white text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <User className="w-3.5 h-3.5" />
                      <span>{currentLang === 'en' ? 'Sign In or Create Account' : currentLang === 'ru' ? 'Войти или Зарегистрироваться' : 'Akkaunt ochish yoki Kirish'}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              messages.map((msg) => {
                const isMine = msg.senderType === 'CLIENT' || (msg.senderId === user?.id);

                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[75%] rounded-2xl p-3 shadow-md ${
                        isMine
                          ? 'bg-[#18263e] border border-[#2a4066] text-slate-100 rounded-tr-xs'
                          : 'bg-[#0e1627] border border-[#1c2e4f] text-slate-200 rounded-tl-xs'
                      }`}
                    >
                      {!isMine && (
                        <span className="text-[10px] font-semibold text-[#c59b27] block mb-1">
                          {msg.senderName}
                        </span>
                      )}

                      {/* File Attachment */}
                      {msg.fileUrl && (
                        <div className="mb-2">
                          {msg.fileType === 'IMAGE' ? (
                            <div className="rounded-lg overflow-hidden border border-slate-700/60 max-h-60 bg-black">
                              <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer">
                                <img src={msg.fileUrl} alt={currentLang === 'en' ? 'Document' : currentLang === 'ru' ? 'Документ' : 'Hujjat'} className="w-full h-auto object-cover hover:opacity-90 transition-opacity" />
                              </a>
                            </div>
                          ) : (
                            <a
                              href={msg.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-2.5 p-2.5 rounded-lg bg-[#070b14] border border-[#1e2f4c] hover:border-[#c59b27] transition-all group"
                            >
                              <FileText className="w-5 h-5 text-red-400 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <span className="text-xs font-semibold text-slate-200 block truncate group-hover:text-[#c59b27]">
                                  {msg.fileName || (currentLang === 'en' ? 'PDF Document' : currentLang === 'ru' ? 'PDF Документ' : 'PDF Hujjat')}
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  {currentLang === 'en' ? 'PDF format • View and download' : currentLang === 'ru' ? 'Формат PDF • Просмотр и скачивание' : 'PDF format • Ko‘rish va yuklab olish'}
                                </span>
                              </div>
                              <Download className="w-4 h-4 text-slate-400 group-hover:text-white shrink-0" />
                            </a>
                          )}
                        </div>
                      )}

                      {/* Text Message */}
                      {msg.text && (
                        <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-line break-words">
                          {msg.text}
                        </p>
                      )}

                      {/* Footer Time & Status */}
                      <div className="flex items-center justify-end space-x-1.5 mt-1 pt-0.5 text-[9px] text-slate-400">
                        <span>
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isMine && (
                          msg.isRead ? (
                            <CheckCheck className="w-3 h-3 text-[#c59b27]" />
                          ) : (
                            <Check className="w-3 h-3 text-slate-400" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Status & Security Prompt Bar Above Input */}
          {!isAuthenticated ? (
            <div className="mx-3 my-1.5 p-2 sm:p-2.5 rounded-xl bg-gradient-to-r from-[#0d1627] via-[#091120] to-[#0d1627] border border-[#c59b27]/30 flex items-center justify-between text-xs text-slate-300 shrink-0 shadow-sm animate-in fade-in duration-200">
              <div className="flex items-center space-x-2 min-w-0 pr-2">
                <Lock className="w-4 h-4 text-[#c59b27] shrink-0" />
                <span className="text-[11px] truncate text-slate-300">
                  {currentLang === 'en'
                    ? 'Sign in or create an account to consult with attorney'
                    : currentLang === 'ru'
                    ? 'Для связи с адвокатом войдите или создайте аккаунт'
                    : 'Advokat bilan bog‘lanish uchun akkauntga kiring yoki oching'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setAuthModalOpen(true)}
                className="px-3 py-1 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:to-[#d4af37] text-slate-950 font-bold text-[10px] uppercase tracking-wider shrink-0 cursor-pointer shadow-md transition-all whitespace-nowrap"
              >
                {currentLang === 'en' ? 'Sign In / Account' : currentLang === 'ru' ? 'Войти / Аккаунт' : 'Kirish / Akkaunt'}
              </button>
            </div>
          ) : !isSubscribed ? (
            <div className="mx-3 my-1.5 p-2 sm:p-2.5 rounded-xl bg-gradient-to-r from-[#141b2b] via-[#0d1524] to-[#141b2b] border border-amber-500/40 flex items-center justify-between text-xs text-slate-300 shrink-0 shadow-sm animate-in fade-in duration-200">
              <div className="flex items-center space-x-2 min-w-0 pr-2">
                <Lock className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="text-[11px] truncate text-slate-300">
                  {currentLang === 'en'
                    ? '35,000 UZS subscription required to send messages'
                    : currentLang === 'ru'
                    ? 'Для отправки сообщения требуется подписка (35 000 сум)'
                    : 'Xabar yuborish uchun 35 000 so‘mlik obuna talab etiladi'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setSubscriptionModalOpen(true)}
                className="px-3 py-1 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:to-[#d4af37] text-slate-950 font-bold text-[10px] uppercase tracking-wider shrink-0 cursor-pointer shadow-md transition-all whitespace-nowrap"
              >
                {currentLang === 'en' ? 'Subscription (35,000 UZS)' : currentLang === 'ru' ? 'Подписка (35 000 UZS)' : 'Obuna (35 000 UZS)'}
              </button>
            </div>
          ) : null}

          {/* Input Bar */}
          <form onSubmit={handleSendMessage} className="p-3 bg-[#090e18] border-t border-[#162238] flex items-center space-x-2 shrink-0">
            
            {/* File Upload Trigger */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              type="button"
              disabled={uploadingFile}
              onClick={() => {
                if (!isAuthenticated) {
                  setAuthModalOpen(true);
                  showToast(
                    currentLang === 'en'
                      ? 'Please sign in before sending files'
                      : currentLang === 'ru'
                      ? 'Для отправки файла сначала войдите'
                      : 'Fayl yuborish uchun avval tizimga kiring',
                    'info'
                  );
                  return;
                }
                if (!isSubscribed) {
                  setSubscriptionModalOpen(true);
                  showToast(
                    currentLang === 'en'
                      ? 'Activate 35,000 UZS subscription to send files'
                      : currentLang === 'ru'
                      ? 'Для отправки файлов требуется подписка'
                      : 'Fayl yuborish uchun 35 000 so‘mlik obunani faollashtiring',
                    'info'
                  );
                  return;
                }
                fileInputRef.current?.click();
              }}
              title={currentLang === 'en' ? 'Attach image (JPG, PNG) or document (PDF)' : currentLang === 'ru' ? 'Прикрепить фото (JPG, PNG) или документ (PDF)' : 'Rasm (JPG, PNG) yoki Hujjat (PDF) biriktirish'}
              className="w-9 h-9 rounded-xl bg-[#0f1728] hover:bg-[#18253d] border border-slate-700 hover:border-[#c59b27] flex items-center justify-center text-slate-400 hover:text-[#c59b27] transition-colors cursor-pointer shrink-0 disabled:opacity-50"
            >
              {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin text-[#c59b27]" /> : <Paperclip className="w-4 h-4" />}
            </button>

            {/* Text input */}
            <input
              type="text"
              placeholder={
                !isAuthenticated 
                  ? (currentLang === 'en'
                      ? 'Type your inquiry (account required before sending)...'
                      : currentLang === 'ru'
                      ? 'Введите ваш вопрос (перед отправкой потребуется аккаунт)...'
                      : 'Savolingizni yozing (yuborishdan oldin akkaunt so‘raladi)...')
                  : !isSubscribed 
                  ? (currentLang === 'en'
                      ? 'Type your inquiry (subscription activation required to send)...'
                      : currentLang === 'ru'
                      ? 'Введите ваш вопрос (потребуется подписка для отправки)...'
                      : 'Savolingizni yozing (yuborishda obuna faollashtiriladi)...')
                  : currentLang === 'en' 
                  ? 'Type confidential message...' 
                  : currentLang === 'ru' 
                  ? 'Введите конфиденциальное сообщение...' 
                  : 'Maxfiy xabar matnini yozing...'
              }
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="flex-1 bg-[#050912] border border-[#162238] focus:border-[#c59b27] rounded-xl px-4 py-2 text-xs sm:text-sm text-slate-100 placeholder-slate-500 focus:outline-none transition-colors"
            />

            {/* Send Button */}
            <button
              type="submit"
              disabled={!inputText.trim() || sending}
              className="w-9 h-9 rounded-xl bg-[#c59b27] hover:bg-[#d4af37] disabled:opacity-40 disabled:hover:bg-[#c59b27] text-slate-950 flex items-center justify-center transition-all cursor-pointer shrink-0 shadow-md"
              title={currentLang === 'en' ? 'Send message' : currentLang === 'ru' ? 'Отправить сообщение' : 'Xabarni yuborish'}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>

        </div>
      </div>

      {/* Account Creation / Login Modal */}
      {authModalOpen && (
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* 30-day Client Subscription Modal (35 000 UZS) */}
      {subscriptionModalOpen && (
        <SubscriptionModal
          isOpen={subscriptionModalOpen}
          onClose={() => setSubscriptionModalOpen(false)}
          initialType="CLIENT"
          onSuccess={handleSubscriptionSuccess}
        />
      )}
    </>
  );
}
